import React from 'react';
import {
  Button,
  Typography,
  Modal,
  ModalBody,
} from '@one-thd/sui-atomic-components';
import { LazyImage } from '../../../../../shared/LazyImage';
import { SimpleCarousel } from '../../SimpleCarousel/SimpleCarousel';
import {
  assetsPricing,
  pbPreferredPricing,
} from '../../../../../../constants/PricingBenefits';
import {
  VideoLabelBtn,
  VideoLabelPlayBtn,
  VideoContainer,
  ModalCloseIcon,
  Img,
  VideoPP,
} from './Shared';
import { clickCta, setOverlayEvent } from '../../../../../../utils/analytics';

interface HowItWorksModalProps {
  isMobile: boolean;
}

interface ModalListItem {
  id: number;
  name: string;
  imgurl: string;
  description: string;
}

interface ModalImagesProps {
  data: ModalListItem;
}

interface RenderDesktopNavigationProps {
  currentIndex: number;
  onHandleClick: (index: number) => void;
}

interface NameMaxWidth {
  [key: string]: { name: string; desc: string };
}

interface StepContentDTProps {
  classes: string;
}

interface BannerContainerProps {
  children: React.ReactNode;
}

interface VisibilityProps {
  isInvisible: boolean;
}

interface VideoModalProps {
  isMobile: boolean;
  rounded: boolean;
  children?: React.ReactNode;
}

const constants = pbPreferredPricing.PrefPricingBanner;
const VIDEO_ID = 5;
const pgnClass = {
  // Pagination classes (Overrides _pagination.scss from global-css repo)
  active: ' sui-cursor-pointer sui-bg-brand sui-text-inverse sui-font-w-bold',
  activeLeft: ' sui-rounded-l-full',
  activeRight: ' sui-rounded-r-full',
  'hd-pagination__desktop-modal':
    'sui-h-56 sui-leading-tight sui-mb-4 sm:sui-mb-0',
  'hd-pagination__wrapper':
    'hd-pagination__wrapper !sui-my-4 !sui-mx-auto sui-list-none',
  'hd-pagination__link':
    'hd-pagination__link !sui-text-lg !sui-py-[5px] !sui-px-[15px] !bg-[length:40%]',
  'hd-pagination__item1': 'sui-m-0 sui-float-left',
};

// For HowItWorks Modal
export const HowItWorksModal = ({
  isMobile,
}: HowItWorksModalProps): JSX.Element => {
  const [toggle, setToggle] = React.useState(false);
  const [filterData] = React.useState(pbPreferredPricing.modallist);
  const [play, setPlay] = React.useState(false);

  const handleVideo = () => {
    return setPlay(!play);
  };

  const ModalImages = ({ data }: ModalImagesProps) => {
    const { id, imgurl } = data;
    return (
      <div>
        {id === VIDEO_ID ? (
          <VideoLabelPlayBtn rounded setModal={handleVideo} />
        ) : (
          <LazyImage
            src={imgurl}
            classes=""
            altText="Top banner, Home Depot store"
            aria-label="image"
          />
        )}
      </div>
    );
  };

  const RenderDesktopNavigation = (
    navProps: RenderDesktopNavigationProps,
  ): JSX.Element => {
    const { currentIndex, onHandleClick } = navProps;
    const activeTab = (props1: { id: number }) => {
      let border;
      if (props1.id === currentIndex + 1) {
        if (currentIndex === 0) {
          border = `${pgnClass.active} ${pgnClass.activeLeft}`;
        } else if (currentIndex === filterData.length - 1) {
          border = `${pgnClass.active} ${pgnClass.activeRight}`;
        } else {
          border = pgnClass.active;
        }
      } else {
        border = '';
      }
      return border;
    };
    const handleLeftClick = () => {
      if (currentIndex > 0) {
        const currentDataNavigation = currentIndex - 1;
        onHandleClick(currentDataNavigation);
      }
    };

    const handleRightClick = () => {
      if (currentIndex < filterData.length - 1) {
        const currentDataNavigation = currentIndex + 1;
        onHandleClick(currentDataNavigation);
      }
    };
    return (
      <div className="sui-text-center">
        <ul className={pgnClass['hd-pagination__wrapper']}>
          <li
            data-testid="hd-pagination__item1"
            className={
              currentIndex === 0
                ? `hd-pagination__button ${pgnClass['hd-pagination__item1']}`
                : ' hd-pagination__button hd-pagination__item'
            }
          >
            <button
              type="button"
              onClick={handleLeftClick}
              aria-label="button"
              className={`${
                currentIndex > 0
                  ? pgnClass['hd-pagination__link']
                  : 'sui-hidden'
              }`}
            >
              &nbsp;
            </button>
          </li>
          {filterData.map((test: ModalListItem) => (
            <li
              key={test.id}
              className={`hd-pagination__item ${activeTab(test)}`}
            >
              <button
                type="button"
                aria-label="button"
                onClick={() => {
                  const newValue = test.id - 1;
                  onHandleClick(newValue);
                }}
                className={pgnClass['hd-pagination__link']}
              >
                {test.id}
              </button>
            </li>
          ))}
          <li className="hd-pagination__item hd-pagination__button">
            <button
              type="button"
              aria-label="button"
              onClick={handleRightClick}
              className={`${
                currentIndex < filterData.length - 1
                  ? pgnClass['hd-pagination__link']
                  : 'sui-hidden'
              }`}
            >
              &nbsp;
            </button>
          </li>
        </ul>
      </div>
    );
  };

  const RenderModalPagination = (): JSX.Element => {
    const [currentDataIndex, setCurrentDataIndex] = React.useState(0);
    const currentDataSelected = filterData[currentDataIndex];
    const { id, name, description } = currentDataSelected;
    const handleClick = React.useCallback(
      (index: number) => {
        setCurrentDataIndex(index);
        // analytics.pushMyAccountButtonEvent(
        //   `pricing: pro xtra preferred pricing: how does it work overlay: swipe`,
        // );
      },
      [currentDataIndex],
    );
    const nameMaxWidth: NameMaxWidth = {
      '1': { name: '190px', desc: '' },
      '2': { name: '220px', desc: '' },
      '3': { name: '', desc: '' },
      '4': { name: '', desc: '' },
      '5': { name: '', desc: '' },
    };

    return (
      <div className="sui-h-full sui-text-base sui-px-6 sui-flex sui-flex-col">
        <div className="sui-py-4">{pbPreferredPricing.modalEasyTitle}</div>
        <div className="sui-grow">
          <div
            data-testid="hd-pagination__desktop-modal"
            className={`sui-grid sui-grid-cols-12 ${pgnClass['hd-pagination__desktop-modal']}`}
          >
            <div className="sui-col-span-5 sui-flex sui-flex-col">
              <div className="sui-text-xl sui-pt-5 sui-font-bold sui-pb-4">
                <p style={{ maxWidth: nameMaxWidth[`${id}`]?.name || 'none' }}>
                  {name}
                </p>
              </div>
              <div className="sui-pb-6">
                <p style={{ maxWidth: nameMaxWidth[`${id}`]?.desc || 'none' }}>
                  {description}
                </p>
              </div>
              {id === VIDEO_ID && !play && (
                <button
                  type="button"
                  className="sui-block sui-invisible"
                  onClick={handleVideo}
                >
                  {pbPreferredPricing.watchButton}{' '}
                  <span className="">
                    <img
                      src={assetsPricing.NextArrow}
                      width="auto"
                      height="auto"
                      alt="img"
                    />
                  </span>
                </button>
              )}
            </div>
            <div className="sui-col-span-1">&nbsp;</div>
            <div className="sui-col-span-6 sui-flex sui-justify-center sui-flex-col">
              <ModalImages data={currentDataSelected} />
            </div>
          </div>
        </div>
        <div>
          <RenderDesktopNavigation
            onHandleClick={handleClick}
            currentIndex={currentDataIndex}
          />
        </div>
      </div>
    );
  };

  const StepContentDT = ({ classes }: StepContentDTProps) => (
    <div
      data-testid="modal-container-dt"
      className={
        classes ??
        'sui-flex sui-flex-col sui-absolute sui-left-0 sui-right-0 sui-top-0 sui-bottom-0 sui-justify-between'
      }
    >
      <div className="sui-grow sui-flex sui-flex-col">
        <div className="sui-grid sui-grid-cols-12 sui-text-3xl sui-py-4 sui-px-6 sui-border-solid sui-border-b-1 sui-border-primary">
          <div className="sui-col-span-11">
            {pbPreferredPricing.modalHowItWork}
          </div>
          <div className="sui-col-span-1 sui-flex sui-justify-end">
            <ModalCloseIcon setModal={setToggle} />
          </div>
        </div>
        <div className="sui-grow">
          <RenderModalPagination />
        </div>
      </div>
      <div className="sui-px-6 sui-border-solid sui-border-t-1 sui-border-primary sui-py-4 sui-text-right">
        <Button
          variant="primary"
          aria-label="button"
          classes="sui-bg-brand sui-h-11"
          onClick={() => setToggle(false)}
        >
          Got It!
        </Button>
      </div>
    </div>
  );
  const ModalDT = () => (
    <Modal open={toggle} size="md" onClose={() => setToggle(false)}>
      <ModalBody>
        <div className="sui-h-full">
          {!play ? (
            <>
              <StepContentDT classes="" />
              <StepContentDT classes="sui-invisible" />
            </>
          ) : (
            <>
              <div
                data-testid="video-container-dt"
                className="sui-h-full sui-bg-inverse sui-pt-7 sui-absolute sui-left-0 sui-right-0 sui-top-0 sui-bottom-0"
              >
                <VideoContainer
                  setModal={() => {
                    handleVideo();
                  }}
                />
              </div>
              <div className="sui-invisible">
                <VideoContainer
                  setModal={() => {
                    handleVideo();
                  }}
                  noAutoPlay
                />
              </div>
            </>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
  const ModalDesktop = (): JSX.Element => {
    const ButtonDT = () => (
      <div className="sui-flex">
        <span>{constants.qualified.content1}</span>
        <button
          type="button"
          aria-label="button"
          className="sui-cursor-pointer"
          onClick={() => {
            setToggle(true);
            setOverlayEvent(
              'pricing benefits preferred pricing how does it work',
            );
          }}
        >
          <span className="sui-pl-3 sui-pr-2">
            {constants.qualified.content2}
          </span>
          <span className="">
            <img
              src={assetsPricing.NextArrow}
              className="sui-h-3 sui-align-baseline"
              width="auto"
              height="auto"
              alt="img"
            />
          </span>
        </button>
      </div>
    );

    return (
      <>
        <ModalDT />
        <ButtonDT />
      </>
    );
  };
  const ModalMobile = (): JSX.Element => {
    const PaintBenefitsCard = (mobilelist: ModalListItem) => {
      const { id, name, imgurl, description } = mobilelist;
      const nameMaxWidth: NameMaxWidth = {
        '1': { name: '', desc: '270px' },
        '2': { name: '250px', desc: '' },
        '3': { name: '200px', desc: '' },
        '4': { name: '', desc: '' },
        '5': { name: '', desc: '250px' },
      };
      return (
        <div className="">
          <div className="sui-grid sui-grid-cols-12 sui-font-bold sui-text-lg sui-mb-5">
            <div className="sui-col-span-1" />
            <div className="sui-col-span-10 sui-flex sui-justify-center">
              <p style={{ maxWidth: nameMaxWidth[`${id}`]?.name || 'none' }}>
                {name}
              </p>
            </div>
          </div>
          <div
            className="sui-flex sui-flex-col sui-justify-center"
            style={{ height: '160px' }}
          >
            {id === VIDEO_ID && !play ? (
              <div className="sui-grid sui-grid-cols-12">
                <div className="sui-col-span-1" />
                <div className="sui-col-span-10">
                  <VideoLabelPlayBtn rounded isDark setModal={handleVideo} />
                </div>
              </div>
            ) : (
              <LazyImage
                src={imgurl}
                width="210"
                height="155"
                classes=""
                aria-label="image"
                altText="Top banner, Home Depot store"
              />
            )}
          </div>
          <div className="sui-grid sui-grid-cols-12 sui-mt-5 sui-pt-2">
            <div className="sui-col-span-1" />
            <div className="sui-col-span-10 sui-flex sui-justify-center">
              <p style={{ maxWidth: nameMaxWidth[`${id}`]?.desc || 'none' }}>
                {description}
              </p>
            </div>
          </div>
        </div>
      );
    };
    const ButtonMW = () => {
      const BannerText = () => (
        <>
          <Typography variant="h3" weight="display" color="inverse">
            {constants.qualified.content1}
          </Typography>
          <p className="sui-mt-2">
            {constants.qualified.content2}
            <Img
              classes="sui-h-3 sui-pl-2 sui-mb-1"
              src={assetsPricing.NextArrowOrange}
            />
          </p>
        </>
      );
      const BannerContainer = ({ children }: BannerContainerProps) => (
        <button
          type="button"
          className="sui-cursor-pointer sui-w-full"
          aria-label="button"
          onClick={() => {
            setToggle(true);
            clickCta('pricing benefits preferred pricing how does it work');
          }}
        >
          {children}
        </button>
      );
      return (
        <BannerContainer>
          <div
            className="sui-relative sui-text-inverse sui-mt-2"
            data-testid="TierQualifiedMobile__Banner"
          >
            <Img src={assetsPricing.bgBrownGradient} classes="sui-w-full" />
            <div className="sui-absolute sui-top-0 sui-right-0 sui-left-0 sui-bottom-0">
              <div className="sui-relative sui-h-full">
                {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
                <div className="sui-w-[49%] sui-h-full sui-absolute sui-right-0 sui-overflow-hidden sui-text-right sui-rounded-r-lg">
                  <Img
                    src={assetsPricing.photoVirtualIdScan}
                    classes="sui-h-full sui--ml-1"
                  />
                </div>
                <div
                  className="sui-absolute sui-top-0 sui-h-full sui-flex sui-flex-col sui-justify-center sui-pl-5 sui-text-left"
                  data-testid="pb-PreferredPricing__TierQualifiedMobile__BannerText"
                >
                  <BannerText />
                </div>
              </div>
            </div>
          </div>
        </BannerContainer>
      );
    };
    const ModalMW = () => {
      const Header = ({ isInvisible }: VisibilityProps) => (
        <div
          className={`sui-border-b-1 sui-border-primary sui-shadow-sm sui-p-3 sui-bg-primary sui-top-0 sui-right-0 sui-left-0 sui-z-10
          ${isInvisible ? 'sui-invisible' : 'sui-fixed'}`}
          data-testid="modal__mobileHeader"
        >
          <div className="sui-grid sui-grid-cols-12">
            <div className="sui-col-span-10 sui-font-bold sui-text-lg">
              How Does It Work?
            </div>
            <div className="sui-col-span-2 sui-text-right">
              <ModalCloseIcon setModal={!isInvisible ? setToggle : undefined} />
            </div>
          </div>
        </div>
      );
      const Footer = ({ isInvisible }: VisibilityProps) => (
        <div
          className={`sui-p-3 sui-border-solid sui-border-t-1 sui-border-primary sui-shadow-sm
          sui-bottom-0 sui-right-0 sui-left-0 sui-bg-primary sui-z-10
          ${isInvisible ? 'sui-invisible' : 'sui-fixed'}`}
          data-testid="modalFooter"
        >
          <Button
            variant="primary"
            classes="sui-bg-brand"
            aria-label="button"
            onClick={!isInvisible ? () => setToggle(false) : undefined}
          >
            Got It!
          </Button>
        </div>
      );
      const TopHero = () => (
        <div className="sui-pt-5">
          <div className="sui-pb-4 sui-relative sui-mb-5 sui-mt-1">
            <div className="">
              <div className="sui-grid sui-grid-cols-12">
                <div className="sui-col-span-2" />
                <div className="sui-col-span-8 sui-border-solid sui-border-primary sui-border-b-1 sui-border-strong">
                  &nbsp;
                </div>
              </div>
            </div>
            <div className="sui-absolute sui-top-0 sui-right-0 sui-left-0">
              <LazyImage
                src={assetsPricing.dollarLogo}
                width="52"
                height="52"
                classes=""
                altText="Top banner, Home Depot store"
                aria-label="image"
              />
            </div>
          </div>
          <div>{pbPreferredPricing.modalEasyTitle}</div>
        </div>
      );
      return (
        <Modal open={toggle} size="lg" onClose={() => setToggle(false)}>
          <ModalBody>
            <div className="sui-h-full">
              {!play ? (
                <div
                  data-testid="modal-container-mw"
                  className="sui-relative sui-h-full sui-text-base"
                >
                  <Header isInvisible={false} />
                  <div className="sui-h-full sui-text-center sui-flex sui-flex-col sui-justify-between">
                    <Header isInvisible />
                    <TopHero />
                    <div className="sui-pt-4 sui-pb-6 sui-h-full">
                      <SimpleCarousel
                        showIndicators
                        items={filterData}
                        render={PaintBenefitsCard}
                        analyticsCall
                        analyticsText="pricing: pro xtra preferred pricing: how does it work overlay: swipe"
                      />
                    </div>
                    <Footer isInvisible />
                  </div>
                  <Footer isInvisible={false} />
                </div>
              ) : (
                <div
                  data-testid="video-container-mw"
                  className="sui-h-full sui-bg-inverse sui-absolute sui-left-0 sui-right-0 sui-top-0 sui-bottom-0"
                >
                  <VideoContainer
                    setModal={() => {
                      handleVideo();
                    }}
                  />
                </div>
              )}
            </div>
          </ModalBody>
        </Modal>
      );
    };
    return (
      <>
        {isMobile ? <ModalMW /> : <ModalDT />}
        <ButtonMW />
      </>
    );
  };
  return isMobile ? <ModalMobile /> : <ModalDesktop />;
};

// For Video Modal file
export const VideoModal = ({
  isMobile,
  rounded,
  children,
}: VideoModalProps): JSX.Element => {
  const [modal, setModal] = React.useState(false);
  const handleClose = () => setModal(false);
  const InvisibleVideo = () => (
    <div className="sui-invisible sui-flex sui-p-6 sui-h-full">
      <div className="sui-pb-3 sui-absolute sui-right-6 sui-top-4">
        <ModalCloseIcon setModal={setModal} />
      </div>
      <VideoPP noAutoPlay />
    </div>
  );
  const ModalDesktop = (): JSX.Element => {
    // Gray X hovers over video
    return (
      <div className="sui-relative">
        <Modal open={modal} size={isMobile ? 'lg' : 'md'} onClose={handleClose}>
          <ModalBody>
            <div className="sui-h-full">
              <div
                data-testid="video-container"
                className="sui-h-full sui-flex sui-justify-center sui-bg-inverse
                sm:sui-px-6 sm:sui-pb-6 sui-absolute sui-pt-12 sui-left-0 sui-right-0 sui-bottom-0"
              >
                <div
                  className="sui-pb-3 sui-absolute sui-right-3 sm:sui-right-6 sui-top-4
                  sui-z-10"
                >
                  <ModalCloseIcon setModal={setModal} />
                </div>
                <VideoPP />
                <div />
              </div>
              <InvisibleVideo />
            </div>
          </ModalBody>
        </Modal>
      </div>
    );
  };
  const DefaultButton = () => {
    return (
      <button
        type="button"
        className="sui-w-full"
        data-testid="videoLogo"
        aria-label="button"
        onClick={() => {
          setModal(true);
          clickCta('pricing benefits: member video view');
        }}
      >
        <VideoLabelBtn rounded={rounded} />
      </button>
    );
  };
  return (
    <>
      <ModalDesktop />
      {children ? { children } : <DefaultButton />}
    </>
  );
};
